import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import Modal from './Modal'
import { handlePatchRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { CLOSE_REASONS } from '../../constants/productFinder'

const CloseProductRequestModal = ({ isOpen, onClose, request, onSuccessCallback }) => {
  const [selectedReason, setSelectedReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  const [error, setError] = useState('')

  const isProductFinder = !!request?.productFinderId
  const requestType = isProductFinder ? 'Product Finder' : 'Bid'

  const getApiEndpoint = () => {
    if (isProductFinder) {
      return `/customers/product-finder/${request.productFinderId}/close`
    } else {
      return `/customers/bids/${request.id}/close`
    }
  }

  const mutation = useMutation({
    mutationFn: (reason) => {
      return handlePatchRequest(getApiEndpoint(), {
        closingReason: reason,
      })
    },
    onSuccess: () => {
      const successMessage = `${requestType} request closed successfully!`
      showToaster('success', successMessage)

      QueryKeys.bidDetails.invalidate(request.id)
      QueryKeys.bidsList.invalidateAll()

      if (onSuccessCallback) {
        onSuccessCallback()
      } else {
        onClose()
      }
    },
    onError: (error) => {
      console.error('Failed to close request:', error)
      showToaster(
        'error',
        `Failed to close ${requestType.toLowerCase()} request. Please try again.`
      )
    },
  })

  const handleReasonChange = (e) => {
    setSelectedReason(e.target.value)
    setError('')
  }

  const handleOtherReasonChange = (e) => {
    setOtherReason(e.target.value)
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!selectedReason) {
      setError('Please select a reason')
      return
    }

    if (selectedReason === CLOSE_REASONS.Other && !otherReason.trim()) {
      setError('Please provide a reason')
      return
    }

    setError('')

    const reasonToSubmit = selectedReason === CLOSE_REASONS.Other ? otherReason : selectedReason
    await mutation.mutateAsync(reasonToSubmit)
  }

  const resetForm = () => {
    setSelectedReason('')
    setOtherReason('')
    setError('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!request?.id) {
    return null
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="flex flex-col items-center w-full">
        <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
          <XMarkIcon className="w-8 h-8 text-white" />
        </div>

        <h2 className="text-center font-bold text-lg mb-2">Close {requestType} Request</h2>

        <p className="text-center text-sm text-gray-700 mb-4">
          Let us know why you're closing this {requestType.toLowerCase()} request. Choose from the
          options below:
        </p>

        <form onSubmit={handleSubmit} className="w-full flex flex-col items-center">
          {error && <p className="text-red-500 text-sm mb-2">{error}</p>}

          <div className="w-full max-w-md">
            <label htmlFor="closeReason" className="block text-sm font-medium text-gray-600 mb-1">
              Select Reason
              <span className="text-red-600">*</span>
            </label>
            <select
              id="closeReason"
              value={selectedReason}
              onChange={handleReasonChange}
              required
              className="block w-full h-10 px-3 py-2 bg-secondary-50 border-b border-black outline-none rounded-md text-sm text-gray-800 focus:outline-none"
            >
              <option value="" disabled>
                Select a reason...
              </option>
              {Object.values(CLOSE_REASONS).map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>

          {selectedReason === CLOSE_REASONS.Other && (
            <div className="w-full max-w-md mt-3">
              <label htmlFor="otherReason" className="block text-sm font-medium text-gray-600 mb-1">
                Other Reason
                <span className="text-red-600">*</span>
              </label>
              <textarea
                id="otherReason"
                value={otherReason}
                onChange={handleOtherReasonChange}
                rows={3}
                required
                placeholder="Please specify your reason..."
                className="block w-full px-3 py-2 bg-secondary-50 border-b border-black outline-none rounded-md text-sm text-gray-800 resize-none"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={mutation.isLoading}
            className="mt-6 w-2/3 h-12 bg-red-600 text-white text-lg font-medium capitalize rounded-md shadow-md flex items-center justify-center hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isLoading ? 'Closing...' : 'Close Request'}
          </button>
        </form>
      </div>
    </Modal>
  )
}

export default CloseProductRequestModal
